/**
 * USAGE
 *
  _fetch.post('/your/endpoint', { title: 'some data in object form' })
    .then(data => console.log('Success!', data))
    .catch(error => console.error('There was an error!', error));
 */

// prevents need to always add catch for unhandled errors
const withDefaultCatch = (fn) => (...args) =>
  fn(...args).catch(err => {
    Toaster.showError("An error occured. Please try again later.")
    console.error('Unhandled fetch error:', err);
    throw err; // still allow external .catch to override
  });

export const _fetch = {
  get: withDefaultCatch(get),
  post: withDefaultCatch(post),
  // Deliberately not wrapped: the streaming caller owns its error UI (it already toasts and
  // discards the partial turn), and an AbortSignal cancellation rejects too, which must not
  // surface as an error toast.
  postStream,
  postForm: withDefaultCatch(postForm),
  postFormData: withDefaultCatch(postFormData),
  downloadFile: withDefaultCatch(downloadFile),
  getToken
};

function get(path, data) {
  checkPath(path);
  let url = path
  if (typeof data === 'object') {
    const params = new URLSearchParams();
    for (const property in data) {
      params.append(property, data[property]);
    }
    url += `?${params}`
  }
  const requestOptions = {
    method: 'GET'
  };
  return fetch(url, requestOptions).then(handleResponse)
}

function post(path, body = {}, options = {}) {
  checkPath(path);
  const {
    headers,
    ...extraOpts
  } = options;

  const disableRedirect = options.disableRedirect || false
  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ENToken': getToken(),
      ...headers,
    },
    ...extraOpts,
    body: typeof body === 'object' ? JSON.stringify(unflatten(body)) : body
  };
  return fetch(path, requestOptions).then((resp) => {
    return handleResponse(resp, disableRedirect)
  })
}

/**
 * POSTs a JSON body and consumes a Server-Sent Events (text/event-stream) response,
 * dispatching each frame to a handler as it arrives. Used for streamed AI chat answers,
 * where deltas must render token-by-token rather than after the full response is buffered.
 *
 * POST (not EventSource/GET) so the message + history — which may contain PHI — travel in the
 * body, never a URL, and so the ENToken header can be sent. Frames are `event:`/`data:` pairs;
 * every `data:` payload is compact JSON (a delta is a JSON string, `done`/`error` are objects).
 *
 * @param {string} path - endpoint URL
 * @param {object|string} body - request payload (objects are JSON-stringified)
 * @param {object} handlers
 * @param {(text: string) => void} [handlers.onDelta] - called per incremental text chunk
 * @param {(payload: object) => void} [handlers.onContentComplete] - called when all content
 *   deltas have been delivered but the terminal payload is still pending
 * @param {(payload: object) => void} [handlers.onDone] - called once with the terminal payload
 * @param {(payload: object) => void} [handlers.onError] - called on a server-sent `error` frame
 * @param {AbortSignal} [handlers.signal] - aborts the in-flight stream
 * @returns {Promise<void>} resolves when the stream ends; rejects on a transport/HTTP failure
 */
async function postStream(path, body = {}, { onDelta, onContentComplete, onDone, onError, signal } = {}) {
  checkPath(path);

  const response = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ENToken': getToken(),
    },
    body: typeof body === 'object' ? JSON.stringify(body) : body,
    signal,
  });

  // Session-expiry redirect, mirroring handleResponse for the buffered helpers.
  const redirectUrl = response.headers.get('EnRedirectUrl');
  if (redirectUrl && redirectUrl.length > 0) {
    window.location = redirectUrl;
    return;
  }

  if (!response.ok || !response.body) {
    throw new Error(response.statusText || 'Streaming request failed');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Frames are separated by a blank line; process every complete one in the buffer.
    let sepIndex;
    while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sepIndex);
      buffer = buffer.slice(sepIndex + 2);
      dispatchSseFrame(frame, { onDelta, onContentComplete, onDone, onError });
    }
  }

  // Flush a trailing frame that arrived without its blank-line terminator (defensive).
  buffer += decoder.decode();
  if (buffer.trim().length > 0) {
    dispatchSseFrame(buffer, { onDelta, onContentComplete, onDone, onError });
  }
}

// Parses one SSE frame (`event:`/`data:` lines) and routes it to the matching handler.
function dispatchSseFrame(frame, { onDelta, onContentComplete, onDone, onError }) {
  let eventName = 'message';
  const dataLines = [];

  for (const line of frame.split('\n')) {
    if (line.startsWith(':')) continue; // keep-alive / comment
    if (line.startsWith('event:')) {
      eventName = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      // A single optional leading space after the colon is part of the SSE framing, not the data.
      dataLines.push(line.slice('data:'.length).replace(/^ /, ''));
    }
  }

  if (dataLines.length === 0) return;

  const raw = dataLines.join('\n');
  let data;
  try {
    data = JSON.parse(raw);
  } catch {
    data = raw;
  }

  if (eventName === 'delta') {
    if (onDelta) onDelta(data);
  } else if (eventName === 'content-complete') {
    // The frame's payload rides along (e.g. the AI chat turn's freshly minted logId, which the
    // escalate-to-HR action posts — available here rather than waiting out the `done` frame's
    // server-side redaction stage).
    if (onContentComplete) onContentComplete(data);
  } else if (eventName === 'done') {
    if (onDone) onDone(data);
  } else if (eventName === 'error') {
    if (onError) onError(data);
  }
}

function postForm(path, formData, options = {}) {
  checkPath(path);
  const {
    headers,
    ...extraOpts
  } = options;
  const disableRedirect = options.disableRedirect || false;
  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      ENToken: getToken(),
      ...headers,
    },
    ...extraOpts,
    body: new URLSearchParams(formData),
  };
  return fetch(path, requestOptions).then((resp) => {
    return handleResponse(resp, disableRedirect)
  })
}

// mainly used for file uploading
function postFormData(path, formData, options = {}) {

  let postData = new FormData()

  if (formData instanceof FormData) {
    postData = formData
  } else if (typeof formData === 'object') {
    for (const field in formData ) {

      // any special type handling here
      if (formData[field] instanceof FileList) {
        for (const file of formData[field]) {
          postData.append(field, file)
        }

      } else {
        postData.append(field, formData[field]);
      }
    }

  } else {
    return console.error('Posted data must be of type FormData or Object')
  }

  checkPath(path);
  const {
    headers,
    ...extraOpts
  } = options;

  const requestOptions = {
    method: 'POST',
    headers: {
      // important to define content type for sending files

      ENToken: getToken(),
      ...headers,
    },
    ...extraOpts,
    body: postData,
  };
  return fetch(path, requestOptions).then(handleResponse)
}

function downloadFile(path, body, fileName = String(Math.floor(Date.now() / 1000)), options = {}) {
  checkPath(path);
  const {
    headers,
    ...extraOpts
  } = options;

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ENToken: getToken(),
      ...headers,
    },
    ...extraOpts,
    body: typeof body === 'object' ? JSON.stringify(body) : body
  };

  return fetch(path, requestOptions)
    .then(async response => {
      if (!response.ok) throw new Error("An error occured during download");
      const blob = await response.blob();

      if (blob.size === 0) throw new Error("Empty file content");

      let downloadName = fileName;
      const disposition = response.headers.get("Content-Disposition");
      const customHeader = response.headers.get("filename"); // try from custom header

      // parsing out the file name from content disposition
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match != null && match[1]) {
          downloadName = match[1].replace(/['"]/g, '');
        }
      } else if (customHeader) {
        downloadName = customHeader;
      }


      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName;
      a.click();
      window.URL.revokeObjectURL(url);
    })
    .catch(err => {
      console.error("Download failed:", err);
      throw err;
    });
}

// Utilities
function handleResponse(response, disableRedirect = false) {
  return response.text().then(text => {
    let data = text
    if (data && isJsonString(data)) {
      data = JSON.parse(data);
    }
    const redirectUrl = response.headers.get('EnRedirectUrl');

    if (
      typeof redirectUrl !== "undefined" &&
      redirectUrl != null &&
      redirectUrl.length > 0 &&
      !disableRedirect
    ) {
      window.location = redirectUrl;
      return Promise.reject();
    }

    if (!response.ok) {
      const error = (data && data.message) || response.statusText;
      return Promise.reject(error);
    }

    return data;
  });
}

function checkPath(path) {
  const type = typeof path;
  if (type !== 'string') {
    throw new TypeError(`The path should be a string, instead received a ${type}`);
  }
}

function getToken() {
  const enToken = document.getElementById('en-session-token')
  if (enToken === null) {
    console.warn('Not finding ENToken')
    return ''
  }
  return enToken.value
}

function isJsonString(str) {
  try {
      JSON.parse(str);
  } catch (e) {
      return false;
  }
  return true;
}

/**
 * Function will unflatten a formdata json object to proper json
 */

function unflatten(obj) {
  const result = {};

  for (const flatKey in obj) {
    const value = obj[flatKey];
    const keys = flatKey.split(/[\[\]\.]+/).filter(Boolean);

    keys.reduce((acc, key, idx) => {
      if (idx === keys.length - 1) {
        acc[key] = value;
        return;
      }

      if (!acc[key]) {
        // Decide whether next key is numeric (for array) or not (for object)
        acc[key] = /^\d+$/.test(keys[idx + 1]) ? [] : {};
      }

      return acc[key];
    }, result);
  }

  return result;
}